import { ReactElement, useState, useEffect } from 'react';
import { Network, TokenAsset } from '@types';
import Button from '@components/Button';
import StackView from '@components/StackView';
import Input from '@components/Input';
import TokenAssetList from '@components/TokenAssetList';
import TokenAssetListItem from '@components/TokenAssetListItem';
import useFilteredTokenAssets from './useFilteredTokenAssets';
import useTokenAssetsContext from '@hooks/useTokenAssetsContext';
import useGenericPopup from '@hooks/useGenericPopup';
import Icon from '@components/Icon';

interface TokenAssetSelectionProps {
  fromNetwork: Network;
  preSelectedTokenAsset?: TokenAsset | null;
  onConfirm(tokenAsset: TokenAsset): void;
  toNetwork: Network;
}

function TokenAssetSelection({
  fromNetwork,
  onConfirm,
  preSelectedTokenAsset,
  toNetwork,
}: TokenAssetSelectionProps): ReactElement {
  const { data: tokenAssets, isLoading, hasError } = useTokenAssetsContext();
  const { showPopup } = useGenericPopup();
  const [query, setQuery] = useState('');
  const [selectedToken, setSelectedToken] = useState<TokenAsset | null>(
    preSelectedTokenAsset || null
  );
  const filteredTokenAssets = useFilteredTokenAssets(
    tokenAssets || [],
    fromNetwork,
    toNetwork,
    query
  );

  useEffect(() => {
    if (hasError) {
      showPopup({
        header: 'Something went wrong',
        body: 'Unable to load Tokens',
      });
    }
  }, [hasError, showPopup]);

  return (
    <StackView direction="vertical" spacing={1}>
      <Input
        onChange={(e): void => setQuery(e.target.value)}
        placeholder="Search by symbol or name"
        value={query}
      />
      <TokenAssetList>
        {isLoading && (
          <div className="flex justify-center text-iconMedium py-2 text-center">
            <Icon.Process className="animate-spin" />
          </div>
        )}
        {filteredTokenAssets.map((tokenAsset) => (
          <TokenAssetListItem
            key={tokenAsset.symbol}
            onClick={(t): void => setSelectedToken(t)}
            selected={tokenAsset.symbol === selectedToken?.symbol}
            token={tokenAsset}
          />
        ))}
      </TokenAssetList>
      <Button
        className="w-full"
        disabled={selectedToken === null}
        onClick={(): void => {
          if (selectedToken) {
            onConfirm(selectedToken);
          }
        }}
        type="button"
      >
        Confirm
      </Button>
    </StackView>
  );
}

export default TokenAssetSelection;
